<template>
    <Page>
        <Container class="max-w-screen-lg">
            <div class="mb-6 flex items-center justify-between gap-4">
                <Title class="!mb-0">Feedback</Title>
                <button
                    class="rounded bg-neutral-200 px-3 py-2 text-sm font-semibold dark:bg-neutral-800"
                    type="button"
                    @click="loadFeedback"
                >
                    Refresh
                </button>
            </div>

            <div class="mb-4 flex flex-wrap gap-2">
                <button
                    v-for="filter in filters"
                    :key="filter.key"
                    class="rounded border px-3 py-2 text-center text-sm font-semibold"
                    :class="activeFilter === filter.key
                        ? 'border-brand-primary-600 bg-brand-primary-600 text-white'
                        : 'border-neutral-300 text-neutral-700 dark:border-neutral-700 dark:text-neutral-200'"
                    type="button"
                    @click="activeFilter = filter.key"
                >
                    {{ filter.label }}
                    <span class="ml-1 text-xs opacity-75">{{ filterCount(filter) }}</span>
                </button>
            </div>

            <Loading v-if="loading" />

            <div v-else-if="visibleFeedback.length === 0" class="rounded bg-neutral-100 p-4 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                No feedback has been submitted yet.
            </div>

            <div v-else class="space-y-4">
                <article
                    v-for="entry in visibleFeedback"
                    :key="entry.id"
                    class="rounded bg-neutral-100 p-4 dark:bg-neutral-800"
                >
                    <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div class="min-w-0 flex-1">
                            <div class="mb-2 flex flex-wrap items-center gap-2">
                                <span class="rounded px-2 py-1 text-xs font-semibold" :class="typeClass(entry.feedback_type)">
                                    {{ typeLabel(entry.feedback_type) }}
                                </span>
                                <span class="rounded bg-neutral-200 px-2 py-1 text-xs font-semibold text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200">
                                    {{ statusLabel(entry.status) }}
                                </span>
                                <span class="text-xs text-neutral-500 dark:text-neutral-400">
                                    {{ formatDate(entry.submitted_at || entry.created_at) }}
                                </span>
                            </div>

                            <div class="whitespace-pre-line text-sm leading-6 text-neutral-900 dark:text-white">
                                {{ entry.description }}
                            </div>

                            <div v-if="entry.steps_to_reproduce" class="mt-3 rounded bg-white p-3 text-sm leading-6 text-neutral-700 dark:bg-neutral-950 dark:text-neutral-300">
                                <div class="mb-1 text-xs font-semibold uppercase text-neutral-500 dark:text-neutral-400">Steps</div>
                                <div class="whitespace-pre-line">{{ entry.steps_to_reproduce }}</div>
                            </div>

                            <div class="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
                                <span v-if="entry.user">
                                    {{ entry.user.first_name }} {{ entry.user.last_name }} &lt;{{ entry.user.email }}&gt;
                                </span>
                                <span v-else>Unknown user</span>
                                <span class="mx-2">/</span>
                                <span>{{ entry.prompt_location || 'unknown prompt' }}</span>
                            </div>
                        </div>

                        <div class="w-full rounded bg-white p-3 text-xs leading-5 text-neutral-600 dark:bg-neutral-950 dark:text-neutral-300 md:w-72">
                            <div class="font-semibold text-neutral-900 dark:text-white">Context</div>
                            <div class="mt-2">User ID: {{ entry.user_id || 'none' }}</div>
                            <div>PostHog: {{ entry.posthog_distinct_id || 'not captured' }}</div>
                            <div>User Agent: {{ entry.user_agent || 'unknown' }}</div>
                            <div v-if="entry.current_url" class="mt-2 break-words">
                                <a :href="entry.current_url" target="_blank" rel="noopener noreferrer">
                                    {{ entry.current_url }}
                                </a>
                            </div>
                        </div>
                    </div>
                </article>
            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue';
import {mapActions} from 'vuex';
import moment from 'moment-timezone';

const filters = [
  {key: 'all', label: 'All', predicate: () => true},
  {key: 'bug_report', label: 'Bugs', predicate: entry => entry.feedback_type === 'bug_report'},
  {key: 'feature_feedback', label: 'Features', predicate: entry => entry.feedback_type === 'feature_feedback'},
  {key: 'general_feedback', label: 'General', predicate: entry => entry.feedback_type === 'general_feedback'},
  {key: 'new', label: 'New', predicate: entry => entry.status === 'new'},
];

export default defineComponent({
  name: 'Feedback',
  created() {
    this.loadFeedback();
  },
  data() {
    return {
      activeFilter: 'all',
      feedback: [],
      filters,
      loading: false,
    };
  },
  computed: {
    visibleFeedback() {
      const filter = filters.find(item => item.key === this.activeFilter);

      return this.feedback.filter(entry => !filter || filter.predicate(entry));
    },
  },
  methods: {
    ...mapActions({
      adminFeedback: 'admin/feedback',
    }),
    filterCount(filter) {
      return this.feedback.filter(entry => filter.predicate(entry)).length;
    },
    formatDate(dateString) {
      if (!dateString) {
        return '';
      }

      return moment(dateString).fromNow();
    },
    async loadFeedback() {
      this.loading = true;

      try {
        const response = await this.adminFeedback();

        this.feedback = response?.data?.data || [];
      } catch (error) {
        window.app.snackbarError(error);
      } finally {
        this.loading = false;
      }
    },
    statusLabel(status) {
      return String(status || 'new').replaceAll('_', ' ');
    },
    typeClass(type) {
      if (type === 'bug_report') {
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
      }

      if (type === 'feature_feedback') {
        return 'bg-brand-primary-100 text-brand-primary-800 dark:bg-brand-primary-900 dark:text-brand-primary-100';
      }

      return 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200';
    },
    typeLabel(type) {
      if (type === 'bug_report') {
        return 'Bug Report';
      }

      if (type === 'feature_feedback') {
        return 'Feature Feedback';
      }

      return 'General Feedback';
    },
  },
});
</script>
